// Hosts the correct "add/edit a single record" side panel for each
// record-based change section (driver, vehicle, coverage, interested party),
// and writes the saved record into the matching values.*_change.records array.
//
// This is what makes the board's "Add" button (and "Edit" in Changes
// Requested) open the record form directly, instead of an intermediate table.
//
// PERFORMANCE NOTE: the substitution pickers need the "existing records" lists
// (drivers, vehicles, coverages, interested parties). Those lists are fetched
// by the useAutomobile* hooks. To avoid firing all four list APIs on
// manage-view load, each section's UI lives in its OWN component below and
// only that component calls its hook. Because a section component is mounted
// only when it is the active `target`, its list API fires only when that
// specific panel opens — and only when actually substituting ("Change" mode).
// Plain "Add"/"Edit" renders the *RightPanel directly and needs no list.

import React from "react";
import produce from "immer";
import { IAutomobilePolicyState } from "../../../../../../../../redux/automobilePolicyChangeReq/automobilePolicyChangeReq.types";
import { AutomobileDriverRightPanel } from "../../../AutomobileDriver/AutomobileDriverInformation/AutomobileDriverRightPanel";
import { AutomobileVehicleInfoRightPanel } from "../../../AutomobileVehicle/AutomobileVehicleInformation/AutomobileVehicleInfoRightPanel";
import { AutomobileScheduleARightPanel } from "../../../AutomobileSchedule/AutomobileScheduleA/AutomobileScheduleARightPanel";
import { AutomobileInterested } from "../../../AutomobileVehicle/AutomobileInterested/AutomobileInterested";
import { ChangeSectionKey } from "./WhatChangedBoard";
import { RecordReplacePanelV2 } from "./RecordReplacePanelV2";
import { AutomobileDriverInformation } from "../../../AutomobileDriver/AutomobileDriverInformation/AutomobileDriverInformation";
import { AutomobileVehicleInformation } from "../../../AutomobileVehicle/AutomobileVehicleInformation/AutomobileVehicleInformation";
import { AutomobileScheduleA } from "../../../AutomobileSchedule/AutomobileScheduleA/AutomobileScheduleA";
import { useAutomobilePolicyVehcileInfo } from "../../../hooks/useAutomobileVehicleInfo";
import { useAutomobilePolicyDriverInfo } from "../../../hooks/useAutomobilePolicyDriverInfo";
import { useAutomobileVehicleInterested } from "../../../hooks/useAutomobileVehicleInterested";
import { useAutomobileCoverageInfo } from "../../../hooks/useAutomobileCoverage";

// Sections that use a record panel (single-object sections are handled inline).
export type RecordSectionKey =
  | "driverChange"
  | "vehicleChange"
  | "coverageChange"
  | "interestedPartyChange";

export const isRecordSection = (
  section: ChangeSectionKey
): section is RecordSectionKey =>
  [
    "driverChange",
    "vehicleChange",
    "coverageChange",
    "interestedPartyChange",
  ].includes(section);

export interface IRecordPanelTarget {
  section: RecordSectionKey;
  // undefined => create a new record; number => edit the record at this index.
  editIndex?: number;
  // Optional mode carried over from the board selection (e.g. "Change" for a
  // vehicle substitution). Falls back to "Add" when not provided.
  mode?: string | null;
}

// Maps a section to the values.*_change slice key + its "type" mode.
const SECTION_TO_SLICE: Record<
  RecordSectionKey,
  "driver_change" | "vehicle_change" | "coverage_change" | "interested_party_change"
> = {
  driverChange: "driver_change",
  vehicleChange: "vehicle_change",
  coverageChange: "coverage_change",
  interestedPartyChange: "interested_party_change",
};

interface IRecordPanelHostProps {
  target: IRecordPanelTarget | null;
  values: IAutomobilePolicyState;
  customerId?: number;
  customerPolicyId?: number;
  setValues: (values: any, shouldValidate?: boolean) => any;
  autoSaveData?: (values: IAutomobilePolicyState) => void;
  onClose: () => void;
}

// Shared props each per-section panel needs.
interface ISectionPanelProps {
  target: IRecordPanelTarget;
  values: IAutomobilePolicyState;
  customerId?: number;
  customerPolicyId?: number;
  isEdit: boolean;
  existing: any;
  setValues: (values: any, shouldValidate?: boolean) => any;
  saveRecord: (record: any) => void;
  onClose: () => void;
}

export const RecordPanelHost: React.FC<IRecordPanelHostProps> = ({
  target,
  values,
  customerId,
  customerPolicyId,
  setValues,
  autoSaveData = () => {},
  onClose,
}) => {
  if (!target) {
    return null;
  }

  const sliceKey = SECTION_TO_SLICE[target.section];
  const isEdit = target.editIndex !== undefined;
  const existing =
    isEdit && (values as any)[sliceKey]?.records
      ? (values as any)[sliceKey].records[target.editIndex as number]
      : undefined;

  // Enriches the saved record and writes it back into the right records array.
  const saveRecord = (record: any) => {
    const newValues = produce(values, (draftValues: any) => {
      // Each record carries its OWN mode ("Add" / "Change") and, for a
      // substitution, its OWN snapshot of the record being replaced. This is
      // what keeps a later "Add" from retroactively flipping earlier "Change"
      // records to ADD in the Changes Requested summary — the section-level
      // `type` flag alone can't distinguish per-record modes.
      const recordMode = target.mode === "Change" ? "Change" : "Add";
      const slice = draftValues[sliceKey] || {};
      const replacedSnapshot =
        recordMode === "Change"
          ? (record as any)?.replaced_record_details ??
            slice.delete_driver_details ??
            slice.delete_vehicle_details ??
            slice.delete_coverage_details ??
            slice.delete_interested_party_details ??
            null
          : undefined;
      const replacedDeleteValue =
        recordMode === "Change"
          ? (record as any)?.replaced_delete_value ??
            slice.delete_driver ??
            slice.delete_vehicle ??
            slice.delete_coverage ??
            slice.delete_interested_party ??
            null
          : null;
      const updatedData = {
        ...record,
        customer_policy_id: customerPolicyId ?? null,
        customer_id: customerId ? Number(customerId) : null,
        // Per-record mode + which existing record this one replaces.
        mode: recordMode,
        replaced_delete_value: replacedDeleteValue,
        replaced_record_details:
          recordMode === "Change" ? replacedSnapshot : null,
      };
      // Defensively initialise the slice if the server returned it as null.
      if (!draftValues[sliceKey]) {
        draftValues[sliceKey] = { type: null, records: [] };
      }
      if (!Array.isArray(draftValues[sliceKey].records)) {
        draftValues[sliceKey].records = [];
      }
      // Default the section mode to "Add" if not already set. When the
      // record panel was opened for a "Change" (substitution) action, keep
      // that mode instead of defaulting to "Add" so it surfaces correctly in
      // "Changes Requested" and is submitted with the right type.
      if (!draftValues[sliceKey].type || target.mode) {
        draftValues[sliceKey].type = target.mode || draftValues[sliceKey].type || "Add";
      }
      if (isEdit) {
        draftValues[sliceKey].records[target.editIndex as number] = updatedData;
      } else {
        draftValues[sliceKey].records.push(updatedData);
      }
    });
    autoSaveData(newValues);
    setValues(newValues);
    onClose();
  };

  const sectionProps: ISectionPanelProps = {
    target,
    values,
    customerId,
    customerPolicyId,
    isEdit,
    existing,
    setValues,
    saveRecord,
    onClose,
  };

  // Only the matching section component mounts, so only its list hook (if any)
  // runs — and only when substituting.
  switch (target.section) {
    case "driverChange":
      return <DriverRecordPanel {...sectionProps} />;
    case "vehicleChange":
      return <VehicleRecordPanel {...sectionProps} />;
    case "coverageChange":
      return <CoverageRecordPanel {...sectionProps} />;
    case "interestedPartyChange":
      return <InterestedPartyRecordPanel {...sectionProps} />;
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Per-section panels. Each is mounted only when its section is the active
// target, so its list hook fires lazily (only when that panel opens, and only
// when substituting). The plain "Add"/"Edit" path renders the *RightPanel
// directly and needs no list.
// ---------------------------------------------------------------------------

const DriverRecordPanel: React.FC<ISectionPanelProps> = ({
  values,
  customerId,
  customerPolicyId,
  isEdit,
  existing,
  target,
  setValues,
  saveRecord,
  onClose,
}) => {
  // "Change" (substitution / replace): a single panel shows the "Driver to
  // Replace" selector at the top and the new-driver form below it. Plain
  // "Add" keeps using the standard driver panel.
  const isSubstitution = target.mode === "Change";
  const { driverDropDown, driverList } = useAutomobilePolicyDriverInfo(
    isSubstitution ? customerPolicyId : undefined
  );

  if (isSubstitution) {
    return (
      <RecordReplacePanelV2
        open={true}
        heading="Driver Change"
        recordLabel="Driver"
        options={driverDropDown}
        valueToReplace={values.driver_change?.delete_driver ?? null}
        disableSelect={isEdit}
        onSelectToReplace={(driverToReplace) => {
          const matched = driverList.find(
            (d) => (d.driver_number ?? "") === String(driverToReplace)
          );
          const newValues = produce(values, (draftValues: any) => {
            if (!draftValues.driver_change) {
              draftValues.driver_change = { type: null, records: [] };
            }
            draftValues.driver_change.type = "Change";
            draftValues.driver_change.delete_driver = driverToReplace;
            draftValues.driver_change.delete_driver_details = {
              driver_number: matched?.driver_number ?? String(driverToReplace),
              driver_name: matched?.driver_name ?? null,
              license_number: matched?.license_number ?? null,
            };
          });
          setValues(newValues);
        }}
        onClose={onClose}
      >
        <AutomobileDriverInformation
          customerId={customerId}
          customerPolicyId={customerPolicyId}
          driverNumber={existing ? (existing.driver_number as any) : undefined}
          onSaveSuccess={saveRecord}
          fromRightPanel
          staticStatusValue="ACTIVE"
          created_from="Policy_Change"
          initialData={existing}
        />
      </RecordReplacePanelV2>
    );
  }

  return (
    <AutomobileDriverRightPanel
      open={true}
      customerId={customerId}
      customerPolicyId={customerPolicyId}
      driverNumber={existing ? (existing.driver_number as any) : undefined}
      onSaveSuccess={saveRecord}
      staticStatusValue="ACTIVE"
      created_from="Policy_Change"
      onClose={onClose}
    />
  );
};

const VehicleRecordPanel: React.FC<ISectionPanelProps> = ({
  values,
  customerId,
  customerPolicyId,
  isEdit,
  existing,
  target,
  setValues,
  saveRecord,
  onClose,
}) => {
  // "Change" (substitution / replace): a single panel shows the "Vehicle to
  // Replace" selector on top and the new-vehicle form below it. Plain "Add"
  // keeps using the standard vehicle panel.
  const isSubstitution = target.mode === "Change";
  const { vehcileDropdown, vehiclesList } = useAutomobilePolicyVehcileInfo(
    isSubstitution ? customerPolicyId : undefined
  );

  if (isSubstitution) {
    return (
      <RecordReplacePanelV2
        open={true}
        heading="Vehicle Change"
        recordLabel="Vehicle"
        options={vehcileDropdown}
        valueToReplace={values.vehicle_change?.delete_vehicle ?? null}
        disableSelect={isEdit}
        onSelectToReplace={(vehicleToReplace) => {
          const matched = vehiclesList.find(
            (v) =>
              String(v.incremental_number ?? "") === String(vehicleToReplace)
          );
          const newValues = produce(values, (draftValues: any) => {
            if (!draftValues.vehicle_change) {
              draftValues.vehicle_change = { type: null, records: [] };
            }
            draftValues.vehicle_change.type = "Change";
            draftValues.vehicle_change.delete_vehicle = vehicleToReplace;
            draftValues.vehicle_change.delete_vehicle_details = {
              vehicle_number:
                matched?.incremental_number != null
                  ? String(matched.incremental_number)
                  : String(vehicleToReplace),
              year: matched?.year ?? null,
              make: matched?.make ?? null,
              model: matched?.model ?? null,
              vin: matched?.vin ?? null,
            };
          });
          setValues(newValues);
        }}
        onClose={onClose}
      >
        <AutomobileVehicleInformation
          customerId={customerId}
          customerPolicyId={customerPolicyId}
          vehicleNumber={
            existing ? (existing.incremental_number as any) : undefined
          }
          onSaveSuccess={saveRecord}
          fromRightPanel
          showOnlySaveButton
          created_from="Policy_Change"
          initialData={existing}
        />
      </RecordReplacePanelV2>
    );
  }

  return (
    <AutomobileVehicleInfoRightPanel
      open={true}
      customerId={customerId}
      customerPolicyId={customerPolicyId}
      vehicleNumber={
        existing ? (existing.incremental_number as any) : undefined
      }
      onSaveSuccess={saveRecord}
      created_from="Policy_Change"
      onClose={onClose}
      initialData={existing}
    />
  );
};

const CoverageRecordPanel: React.FC<ISectionPanelProps> = ({
  values,
  customerId,
  customerPolicyId,
  isEdit,
  existing,
  target,
  setValues,
  saveRecord,
  onClose,
}) => {
  // "Change" (substitution / replace): a single panel shows the "Coverage to
  // Replace" selector on top and the new-coverage form below it. Plain "Add"
  // keeps using the standard coverage panel.
  const isSubstitution = target.mode === "Change";
  const { coverageDropdown, listData: coverageList } = useAutomobileCoverageInfo(
    isSubstitution ? customerPolicyId : undefined
  );

  if (isSubstitution) {
    return (
      <RecordReplacePanelV2
        open={true}
        heading="Coverage Change"
        recordLabel="Coverage"
        options={coverageDropdown}
        valueToReplace={values.coverage_change?.delete_coverage ?? null}
        disableSelect={isEdit}
        onSelectToReplace={(coverageToReplace) => {
          const matched = coverageList.find(
            (c: any) =>
              String(c.incremental_number ?? "") === String(coverageToReplace)
          );
          const newValues = produce(values, (draftValues: any) => {
            if (!draftValues.coverage_change) {
              draftValues.coverage_change = { type: null, records: [] };
            }
            draftValues.coverage_change.type = "Change";
            draftValues.coverage_change.delete_coverage = coverageToReplace;
            draftValues.coverage_change.delete_coverage_details = {
              incremental_number:
                matched?.incremental_number != null
                  ? String(matched.incremental_number)
                  : String(coverageToReplace),
              coverage_name: matched?.coverage_name ?? null,
              vin: matched?.vin ?? null,
            };
          });
          setValues(newValues);
        }}
        onClose={onClose}
      >
        <AutomobileScheduleA
          customerId={customerId}
          customerPolicyId={customerPolicyId}
          incrementalNumber={
            existing ? (existing.incremental_number as any) : undefined
          }
          vehicleNumber={
            existing ? (existing.vehicle_number as any) : undefined
          }
          onSaveSuccess={saveRecord}
          fromRightPanel
          created_from="Policy_Change"
          initialData={existing}
          isVehicleEnabled
          isPolicyChange
        />
      </RecordReplacePanelV2>
    );
  }

  return (
    <AutomobileScheduleARightPanel
      open={true}
      values={values}
      customerId={customerId}
      customerPolicyId={customerPolicyId}
      incrementalNumber={
        existing ? (existing.incremental_number as any) : undefined
      }
      vehicleNumber={existing ? (existing.vehicle_number as any) : undefined}
      onSaveSuccess={saveRecord}
      created_from="Policy_Change"
      onClose={onClose}
    />
  );
};

const InterestedPartyRecordPanel: React.FC<ISectionPanelProps> = ({
  values,
  customerId,
  customerPolicyId,
  isEdit,
  existing,
  target,
  setValues,
  saveRecord,
  onClose,
}) => {
  // The interested party form is a Dialog (not a right-panel form body), so —
  // unlike driver/vehicle — the "Interested Party to Replace" selector is
  // rendered inline at the top of that same Dialog (via the substitution*
  // props). The existing-parties list is only needed in substitution mode, so
  // the hook only fetches then.
  const isSubstitution = target.mode === "Change";
  const { interestedDropdown, interestedPartyList } =
    useAutomobileVehicleInterested(isSubstitution ? customerPolicyId : undefined);

  const handleSelectToReplace = (interestedPartyToReplace: string | number) => {
    const matched = interestedPartyList.find(
      (ip) => String(ip.number ?? "") === String(interestedPartyToReplace)
    );
    const newValues = produce(values, (draftValues: any) => {
      if (!draftValues.interested_party_change) {
        draftValues.interested_party_change = { type: null, records: [] };
      }
      draftValues.interested_party_change.type = "Change";
      draftValues.interested_party_change.delete_interested_party =
        interestedPartyToReplace;
      draftValues.interested_party_change.delete_interested_party_details = {
        number:
          matched?.number != null
            ? String(matched.number)
            : String(interestedPartyToReplace),
        interested_party_code: matched?.interested_party_code ?? null,
        name: matched?.name ?? null,
      };
    });
    setValues(newValues);
  };

  // Consistent with driver/vehicle/coverage: save through the panel's normal
  // flow (no disableApiCalls) so all record sections behave the same, then the
  // saved record is written into the records array.
  return (
    <AutomobileInterested
      open={true}
      customerId={customerId}
      customerPolicyId={customerPolicyId}
      number={existing ? (existing.number as any) : undefined}
      code={existing ? (existing.interested_party_code as any) : undefined}
      data={existing}
      initialData={existing}
      onComplete={saveRecord}
      onClose={onClose}
      created_from="Policy_Change"
      substitutionMode={isSubstitution}
      substitutionOptions={interestedDropdown}
      substitutionValue={
        values.interested_party_change?.delete_interested_party ?? null
      }
      substitutionDisabled={isEdit}
      onSelectSubstitutionTarget={handleSelectToReplace}
    />
  );
};
